import styled, { css } from "styled-components";
import { useEffect, useState } from "react";
import SidebarItem from "./SidebarItem";
import { useAuth } from "../../context/AuthContext";
import { Link } from "react-router";
import { getCorrectionRequests } from "../../api/reportsApi";
import {
  IconCalendar,
  IconClockHour4,
  IconReportAnalytics,
  IconUsers,
} from "@tabler/icons-react";

interface SidebarProps {
  sidebarOpen: boolean;
  /** Dar ekran: akıştan çıkıp içeriğin üstüne binen çekmece kipi (bkz. App.tsx). */
  isCompact?: boolean;
  companyLogo?: string;
  onLogoutClick: () => void;
}

const Sidebar = ({
  sidebarOpen,
  isCompact = false,
  companyLogo,
  onLogoutClick,
}: SidebarProps) => {
  const { company, user } = useAuth();

  /*
   * Bekleyen çalışan bildirimi sayısı (plan 2.59) — menüdeki rozet.
   *
   * ⚠ Neden burada: kuyruk İK'nın iş kuyruğu ama hangi sayfada olursa olsun
   * "bekleyen var mı" bilgisinin görünür olması gerekiyor. Ana sayfa kartı
   * yalnızca ana sayfada duruyor.
   *
   * ⚠ Tek seferlik: kabuk zaten oturum boyunca bir kez kuruluyor. Sürekli
   * yoklamak bütün sayfalara arka plan isteği eklerdi; sayı bayatlarsa
   * kuyruk sayfası her açılışta güncelini gösteriyor.
   */
  const [bekleyenBildirim, setBekleyenBildirim] = useState(0);

  useEffect(() => {
    let iptal = false;
    (async () => {
      try {
        const { data }: any = await getCorrectionRequests("pending");
        if (!iptal) setBekleyenBildirim((data?.data ?? []).length);
      } catch {
        // Sessiz geç: rozet bir kolaylık, menünün ön koşulu değil.
      }
    })();
    return () => {
      iptal = true;
    };
  }, []);

  return (
    <StyledNav $isOpen={sidebarOpen} $isCompact={isCompact}>
      <LogoWrapper $isOpen={sidebarOpen}>
        <Link to="/dashboard" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          {sidebarOpen ? (
            <img height={45} style={{ objectFit: "contain", cursor: "pointer" }} src={"/assets/logo.png"} alt="Logo" />
          ) : (
            <img height={40} style={{ objectFit: "contain", cursor: "pointer" }} src={"/assets/kronifyLogo.png"} alt="Logo" />
          )}
        </Link>
      </LogoWrapper>

      <NavbarItemsWrapper $isOpen={sidebarOpen}>
        <SidebarItem
          iconSrc="/assets/dashboardIcon.png"
          navigateTo="/dashboard"
          title="Özet Rapor"
          sidebarOpen={sidebarOpen}
        />

        {company?.modules?.isCalendarAvailable && (
          <SidebarItem
            icon={<IconCalendar size={20} />}
            navigateTo="/calendar"
            title="Takvim"
            sidebarOpen={sidebarOpen}
          />
        )}

        <SidebarItem
          iconSrc="/assets/employeesIcon.png"
          navigateTo="/employees"
          title="Çalışanlar"
          sidebarOpen={sidebarOpen}
        />

        <SidebarItem
          iconSrc="/assets/terminalIcon.png"
          navigateTo="/tablets"
          title="Tabletler"
          sidebarOpen={sidebarOpen}
        />

        {/* Vardiyalar (plan 2.24): menüye ilk kez burada bağlandı. Önceki
            ekranlar backend'de hiç karşılığı olmayan /weekly-shift uçlarına
            baktığı için bilerek linksiz bırakılmıştı; uçlar canlı olduğu için
            artık görünür. Takvim'den ayrı bir madde: orası işgücü planı
            ("bu güne kaç kişi"), burası çalışma düzeni ("kim hangi düzende"). */}
        <SidebarItem
          icon={<IconClockHour4 size={20} />}
          navigateTo="/shifts"
          title="Vardiyalar"
          isCollapsible
          subItems={[
            { title: "Vardiya Listesi", navigateTo: "work-shift" },
            { title: "Personel Atama", navigateTo: "shift-assignment" },
            // Ekip + rota (plan 2.33/2.34). "Bu Hafta Kim Nerede" herkese
            // açık; kurma ekranları yalnızca admin'de görünüyor — rota kurmak
            // organizasyon kararı, backend de admin'e kilitli.
            { title: "Bu Hafta Kim Nerede", navigateTo: "rotation-board" },
            ...(user?.privilege === "admin"
              ? [
                  { title: "Ekipler", navigateTo: "teams" },
                  { title: "Rotalar", navigateTo: "rotations" },
                ]
              : []),
          ]}
          sidebarOpen={sidebarOpen}
        />

        {/* Raporlar (plan 2.22): route'ları vardı ama menüde yoktu ve arka uçları
            404 dönüyordu. "Gecikme Listesi" bilerek eklenmedi — o ekran hâlâ
            boş bir stub (bkz. 2.22 kararları). */}
        <SidebarItem
          icon={<IconReportAnalytics size={20} />}
          navigateTo="/reports"
          title="Raporlar"
          isCollapsible
          badge={bekleyenBildirim}
          subItems={[
            { title: "Aylık Rapor", navigateTo: "monthly-report" },
            { title: "Giriş/Çıkış Raporu", navigateTo: "entry-exit-reports" },
            {
              title: "Çalışan Bildirimleri",
              navigateTo: "correction-requests",
              badge: bekleyenBildirim,
            },
          ]}
          sidebarOpen={sidebarOpen}
        />

        {user?.privilege === "admin" && (
          <SidebarItem
            iconSrc="/assets/usersIcon.png"
            navigateTo="/panel-users"
            title="Panel Kullanıcıları"
            sidebarOpen={sidebarOpen}
          />
        )}

        {company?.modules?.isApplicantAvailable && (
          <SidebarItem
            icon={<IconUsers size={20} />}
            navigateTo="/human-resources"
            title="İnsan Kaynakları"
            isCollapsible
            subItems={[{ title: "CV Havuzu", navigateTo: "cv-pool" }]}
            sidebarOpen={sidebarOpen}
          />
        )}

        {user?.privilege === "admin" && (
          <SidebarItem
            iconSrc="/assets/settingsIcon.png"
            navigateTo="/settings"
            title="Ayarlar"
            isCollapsible
            subItems={[
              { title: "Genel Ayarlar", navigateTo: "general" },
              { title: "Departman Ayarları", navigateTo: "department" },
              // Tek madde: sayfa zaten iki sekmeli, menüde ikiye bölmek aynı ekranı
              // iki ayrı yermiş gibi gösteriyordu.
              { title: "Rol ve Yetenek Ayarları", navigateTo: "tags" },
              { title: "Kontrat Ayarları", navigateTo: "contract" },
            ]}
            sidebarOpen={sidebarOpen}
          />
        )}
      </NavbarItemsWrapper>
    </StyledNav>
  );
};

export default Sidebar;

const StyledNav = styled.nav<{ $isOpen: boolean; $isCompact: boolean }>`
  width: ${(p) => (p.$isOpen ? "280px" : "64px")};
  min-width: ${(p) => (p.$isOpen ? "280px" : "64px")};
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #d9d9d9;
  height: 100vh;
  height: 100dvh;
  flex-shrink: 0;
  transition: width 0.3s ease, min-width 0.3s ease;
  overflow: hidden;

  /*
   * ÇEKMECE KİPİ (dar ekran).
   *
   * Dar ekranda "daraltılmış 64px" diye bir ara hâl YOK: 64px'lik ikon şeridi
   * telefonda hem yer yiyor hem de etiketsiz ikonlar okunmuyor. Menü ya
   * tamamen görünür ya da tamamen dışarıda.
   *
   * position: fixed — akıştan çıkar, içeriğe hiç yer kaybettirmez.
   * visibility — kapalıyken sekme (Tab) sırasından da çıksın; sadece
   * translateX ile kaydırmak ekran okuyucuya ve klavyeye görünür bırakıyor.
   */
  ${(p) =>
    p.$isCompact &&
    css`
      position: fixed;
      top: 0;
      left: 0;
      bottom: 0;
      z-index: 200;
      width: min(280px, 82vw);
      min-width: 0;
      transform: translateX(${p.$isOpen ? "0" : "-100%"});
      visibility: ${p.$isOpen ? "visible" : "hidden"};
      transition: transform 0.25s ease, visibility 0.25s ease;
      box-shadow: 0 0 40px rgba(17, 24, 39, 0.18);
    `}
`;

const LogoWrapper = styled.div<{ $isOpen: boolean }>`
  padding: 0 20px;
  padding-top: ${(p) => (p.$isOpen ? "20px" : "10px")};
  display: flex;
  align-items: center;
  height: 90px;
  flex-shrink: 0;
  justify-content: center;
`;

const NavbarItemsWrapper = styled.div<{ $isOpen: boolean }>`
  padding: ${(p) => (p.$isOpen ? "24px 16px" : "24px 8px")};
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex-grow: 1;
  overflow-y: auto;
`;